import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import { isVideoTrimmingEnabled } from 'bundles/authoring/featureFlags';
import type {
  OnDemandLectureVideosV1GetQuery,
  OnDemandLectureVideosV1GetQueryVariables,
} from 'bundles/naptimejs/resources/__generated__/OnDemandLectureVideosV1';
import { getVideoStartTimeOffsetSeconds, getVideoTrimmedDurationSeconds, toSeconds } from 'bundles/video-trim';

const query = gql`
  query GetOnDemandLectureVideosV1($id: String!) {
    OnDemandLectureVideosV1 @naptime {
      get(id: $id) {
        elements {
          id
          itemId
          startMs
          endMs
        }
      }
    }
  }
`;

export const useGetTrimRangeQuery = ({ itemId, courseId }: { itemId: string; courseId: string }) => {
  const { data, loading, error } = useQuery<OnDemandLectureVideosV1GetQuery, OnDemandLectureVideosV1GetQueryVariables>(
    query,
    {
      variables: {
        id: `${courseId}~${itemId}`,
      },
      skip: !itemId || !courseId,
    }
  );

  const isEnabled = isVideoTrimmingEnabled();

  const trimRangeData = useMemo(() => {
    const videoData = data?.OnDemandLectureVideosV1?.get?.elements?.[0];

    const startMs = isEnabled ? videoData?.startMs ?? null : null;
    const endMs = isEnabled ? videoData?.endMs ?? null : null;

    return {
      startTimeSeconds: getVideoStartTimeOffsetSeconds(startMs),
      endTimeSeconds: endMs ? toSeconds(endMs) : endMs,
      trimmedDurationSeconds: getVideoTrimmedDurationSeconds(startMs, endMs),
    };
  }, [data?.OnDemandLectureVideosV1?.get?.elements, isEnabled]);

  return { loading, error, trimRangeData };
};
